<template>
    <div v-if="loading">
        <DxLoadPanel :visible="true" :show-indicator="true" :show-pane="true" :shading="false" :hide-on-outside-click="false" />
    </div>
    <div v-else class="request-form-container">
        <div class="propHeader">Name: {{ getContainerVal(container.containerName) }}</div>
        <div class="propHeader">Downhole: {{ getContainerVal(container['Downhole LSD']) }}</div>
        <div class="propHeader">Device: {{ getContainerVal(container['serialnum']) }}</div>
        <div class="propHeader">Volume: {{ getContainerVal(container['Volume (%)']) }}% / {{
            getContainerVal(container['Volume (L)']) }}L / {{ getContainerVal(container['Volume (cm)']) }}cm</div>
        <div class="propHeader">Product: {{ getContainerVal(container['Product']) }}</div>
        <hr />

        <div class="propHeader" v-if="selectedRequest === 'calibration'">
            <DxTextBox id="level" label="Level Height (cm)" @value-changed="infoChanged" />
        </div>
        <div class="propHeader" v-if="selectedRequest === 'calibration'">
            <DxDateBox id="levelDate" label="Date/Time" type="datetime" :max="maxLevelDate"
                @value-changed="infoChanged" />
        </div>
        <div class="propHeader" v-if="selectedRequest === 'calibration'">
            <div v-if="timezoneNeedsAttention" class="timezone-warning">
                GPS for the device could not be found. The time zone must be entered manually.
            </div>
            <DxSelectBox id="timezone" label="Time Zone" :data-source="timezoneList" :search-enabled="true"
                :value="selectedTimezone" @value-changed="infoChanged"
                hint="Enter the time zone that the device is located in."
                :element-attr="{ class: `{ 'timezone-required': timezoneNeedsAttention }` }" />
        </div>
        <div class="propHeader" v-if="selectedRequest === 'product'">
            <DxTextBox id="product" label="New product" @value-changed="infoChanged" />
        </div>
        <div class="propHeader" v-if="selectedRequest === 'tank'">
            <DxTextBox id="name" label="New Tank Name" @value-changed="infoChanged" />
        </div>
        <div class="propHeader" v-if="selectedRequest === 'tank'">
            <DxTextBox id="downhole" label="New Downhole LSD" @value-changed="infoChanged" />
        </div>
        <div class="propHeader" v-if="selectedRequest === 'tank'">
            <DxTextBox id="capacity" label="New Capacity" @value-changed="infoChanged" />
        </div>

        <hr />
        <div class="buttons">
            <DxButton class="saveBtn" :disabled="!canSubmit" icon="email" text="Send" @click="handleSubmit" />
            <DxButton class="cancelBtn" text="Cancel" @click="handleCancel" />
        </div>
    </div>
</template>

<script>
import DxButton from 'devextreme-vue/button';
import DxTextBox from 'devextreme-vue/text-box';
import DxDateBox from 'devextreme-vue/date-box';
import DxSelectBox from 'devextreme-vue/select-box';
import { DxLoadPanel } from 'devextreme-vue/load-panel';

export default {
    name: 'ContainerRequestForm',
    components: {
        DxButton,
        DxTextBox,
        DxDateBox,
        DxSelectBox,
        DxLoadPanel,
    },
    props: {
        container: {
            type: Object,
            required: true
        },
        selectedRequest: {
            type: String,
            required: true
        }
    },
    emits: ['request-submitted', 'request-cancelled'],
    data() {
        return {
            reqLevel: "",
            reqLevelDate: "",
            rawLevelDate: "",
            reqProduct: "",
            reqName: "",
            reqDownhole: "",
            reqCapacity: "",
            reqTimezone: "",
            selectedTimezone: "",
            timezoneDetectionFailed: false,
            loading: false,
        };
    },
    async created() {
        if (this.selectedRequest === 'calibration') {
            this.loading = true;
            await this.initTimezone();
            this.loading = false;
        }
    },
    computed: {
        timezoneList() {
            return this.$store.getters['timezone/allAmericanTimezones'];
        },
        maxLevelDate() {
            return this.selectedTimezone ? this.nowInTimezone(this.selectedTimezone) : new Date();
        },
        timezoneNeedsAttention() {
            return this.timezoneDetectionFailed && !this.reqTimezone;
        },
        canSubmit() {
            if (this.selectedRequest === "calibration"
                && this.reqLevel !== ""
                && this.reqLevelDate !== ""
                && this.reqTimezone !== "")
                return true;

            if (this.selectedRequest === "product"
                && this.reqProduct.length > 1)
                return true;

            if (this.selectedRequest === "tank"
                && (this.reqName !== ""
                    || this.reqDownhole !== ""
                    || this.reqCapacity !== ""))
                return true;

            return false;
        }
    },
    methods: {
        getContainerVal(propVal) {
            if (propVal !== undefined && propVal !== null && propVal !== '') {
                return propVal;
            }
            return '-';
        },
        handleSubmit() {
            const request = {
                containerId: this.container.containerId,
                containerName: this.container.containerName,
                requestType: this.selectedRequest,
                requestInfo: this.getAllRequestInfo()
                
            };

            this.$emit('request-submitted', request);
            this.resetAllRequestInfo();
        },
        handleCancel() {
            this.resetAllRequestInfo();
            this.$emit('request-cancelled');
        },
        resetAllRequestInfo() {
            this.reqLevel = "";
            this.reqLevelDate = "";
            this.rawLevelDate = "";
            this.reqProduct = "";
            this.reqName = "";
            this.reqDownhole = "";
            this.reqCapacity = "";
            this.reqTimezone = "";
            this.selectedTimezone = "";
        },
        getAllRequestInfo() {
            const info = {};
            if (this.reqLevel !== "") info.level = this.reqLevel;
            if (this.reqLevelDate !== "") info.levelDate = this.reqLevelDate;
            if (this.reqProduct !== "") info.product = this.reqProduct;
            if (this.reqName !== "") info.name = this.reqName;
            if (this.reqDownhole !== "") info.downhole = this.reqDownhole;
            if (this.reqCapacity !== "") info.capacity = this.reqCapacity;
            if (this.reqTimezone !== "") info.timeZone = this.reqTimezone;
            if (this.rawLevelDate !== "" && this.reqTimezone !== "")
                info.levelDateCalgaryTime = this.formatInEdmontonTime(this.rawLevelDate, this.reqTimezone);
            return JSON.stringify(info);
        },
        async initTimezone() {
            const latitude = this.container?.latitude;
            const longitude = this.container?.longitude;
            const tzId = await this.$store.dispatch('timezone/lookupTimezone', { latitude, longitude });

            if (tzId && this.$store.getters['timezone/isNorthAmerican'](tzId)) {
                this.selectedTimezone = tzId;
                this.reqTimezone = tzId;
                this.timezoneDetectionFailed = false;
            } else {
                this.timezoneDetectionFailed = true;
            }
        },
        formatLocalDateTime(value) {
            if (!value) return "";
            const date = new Date(value);
            const pad = (n) => String(n).padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        },
        // Returns a Date object whose *local* (device) getters read the current
        // wall-clock time as it appears in timeZone right now -- e.g. if the
        // device is in Edmonton (MST) but timeZone is America/Regina, this
        // returns a Date that reads an hour later than `new Date()` would.
        nowInTimezone(timeZone) {
            const now = new Date();
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone,
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                hour12: false,
            }).formatToParts(now);

            const get = (type) => Number(parts.find((p) => p.type === type)?.value);

            return new Date(
                get('year'),
                get('month') - 1,
                get('day'),
                get('hour') % 24,
                get('minute'),
                get('second')
            );
        },
        // Re-reads the wall-clock digits of `date` (as typed, e.g. "2026-09-14 18:02")
        // as if they were a reading in `fromTimeZone`, then formats that same instant
        // as it appears in America/Edmonton, with the zone abbreviation attached.
        formatInEdmontonTime(date, fromTimeZone) {
            const d = new Date(date);
            const asIfUtc = Date.UTC(
                d.getFullYear(), d.getMonth(), d.getDate(),
                d.getHours(), d.getMinutes(), d.getSeconds()
            );
            const fromZoneOffsetMs = this.getTimezoneOffsetMs(asIfUtc, fromTimeZone);
            const instant = new Date(asIfUtc - fromZoneOffsetMs);

            return new Intl.DateTimeFormat('en-CA', {
                timeZone: 'America/Edmonton',
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit',
                hour12: false,
                timeZoneName: 'short',
            }).format(instant).replace(',', '');
        },
        // Offset (ms) of `timeZone` from UTC at the instant `utcMs`, found by reading
        // that instant's wall clock in `timeZone` and comparing it back to UTC.
        getTimezoneOffsetMs(utcMs, timeZone) {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone, hourCycle: 'h23',
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
            }).formatToParts(new Date(utcMs)).reduce((acc, p) => {
                if (p.type !== 'literal') acc[p.type] = p.value;
                return acc;
            }, {});

            const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
            return asUTC - utcMs;
        },
        infoChanged(e) {
            if (e.element.id === 'level')
                this.reqLevel = e.value;
            else if (e.element.id === 'levelDate') {
                // Plain local wall-clock string (no UTC/'Z' suffix) so it matches
                // exactly what's shown in the box; timezone is tracked separately.
                this.rawLevelDate = e.value;
                this.reqLevelDate = this.formatLocalDateTime(e.value);
            }
            else if (e.element.id === 'product')
                this.reqProduct = e.value;
            else if (e.element.id === 'name')
                this.reqName = e.value;
            else if (e.element.id === 'downhole')
                this.reqDownhole = e.value;
            else if (e.element.id === 'capacity')
                this.reqCapacity = e.value;
            else if (e.element.id === 'timezone') {
                this.selectedTimezone = e.value;
                this.reqTimezone = e.value || "";
            }
        },
    },
};
</script>

<style scoped>
.request-form-container {
    width: 100%;
}

.propHeader {
    font-size: 16px;
    font-weight: 300;
    color: rgb(255, 255, 255, .7);
    margin-bottom: 6px;
    z-index: 20;
}

.timezone-warning {
    color: darkorange;
    font-size: 13px;
    font-weight: 400;
    margin-bottom: 5px;
}

.timezone-required {
    outline: 2px solid darkorange;
   /*  outline-offset: 2px; */
    border-radius: 4px;
}

hr {
    height: 1px;
    border: 1px solid #444;
}

.buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 15px;
}

.saveBtn {
    color: black;
    background-color: #ff7300;
    width: 100px;
}

.cancelBtn {
    color: black;
    background-color: rgb(255, 255, 255, 0.3);
    width: 100px;
}
</style>
